import { directoryApi } from '@my/directory-api';
import type { ImportOrderInput, ImportOrdersResult } from '@my/tms-api';
import type { AddressResolution } from './addresses';
import { isResolvable, type OrderDraft } from './types';

/** Dependencies the commit step injects (hooks can't live in this plain module). */
export interface CommitDeps {
  organisationId: string;
  /** Geocode outcomes from the preview step, keyed by trimmed address text. */
  addressResolutions: Map<string, AddressResolution>;
  /** Allocate one order number from the org sequence (wraps the consume mutation). */
  consumeOrderNumber: () => Promise<string>;
  /** Send the resolved trees to the bulk endpoint (wraps `useImportOrders`). */
  importOrders: (orders: ImportOrderInput[]) => Promise<ImportOrdersResult>;
  onProgress?: (done: number, total: number, phase: string) => void;
}

/** `"16:03"` → `"16:03:00"`; blank → null (the BE expects `HH:mm:ss`). */
function timeOrNull(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  return /^\d{2}:\d{2}$/.test(t) ? `${t}:00` : t;
}

function dateOrNull(raw: string): string | null {
  const d = raw.trim();
  return d.length > 0 ? d : null;
}

/**
 * Commit the committable drafts (included + resolved + not already created):
 *   1. turn each distinct address into an id — reuse an existing org address
 *      (matched by place id in the preview), or create a new one **once per
 *      place id** so two spellings of the same place don't duplicate,
 *   2. allocate an order number per order (reusing one kept from a prior attempt),
 *   3. build the order trees and POST them to the bulk endpoint,
 *   4. write each per-order outcome back via `patch`.
 */
export async function commitOrders(
  drafts: OrderDraft[],
  deps: CommitDeps,
  patch: (key: string, p: Partial<OrderDraft>) => void,
): Promise<void> {
  const committable = drafts.filter(
    (d) => !d.excluded && isResolvable(d) && d.status_ !== 'created',
  );
  if (committable.length === 0) return;

  committable.forEach((d) => patch(d.key, { status_: 'creating', error: undefined }));

  // ── 1. Resolve every distinct address to an id (reuse existing, create new
  //       once per place id). `addressIdFor` caches by place id so repeated or
  //       differently-spelled addresses that geocode to the same place collapse
  //       to one row. ───────────────────────────────────────────────────────
  const distinct = new Set<string>();
  for (const d of committable) {
    if (d.job.collectionAddress.trim()) distinct.add(d.job.collectionAddress.trim());
    if (d.job.deliveryAddress.trim()) distinct.add(d.job.deliveryAddress.trim());
  }

  const idByText = new Map<string, string>();
  const errByText = new Map<string, string>();
  const createdByPlaceId = new Map<string, string>();
  const distinctList = [...distinct];
  let created = 0;
  deps.onProgress?.(0, distinctList.length, 'Resolving addresses');
  for (const text of distinctList) {
    const res = deps.addressResolutions.get(text);
    try {
      if (!res || res.status === 'failed') {
        errByText.set(text, res?.error ?? 'Address not geocoded');
      } else if (res.status === 'matched' && res.existingId) {
        idByText.set(text, res.existingId);
      } else if (res.status === 'new' && res.geocoded) {
        const pid = res.geocoded.placeId;
        const existing = createdByPlaceId.get(pid);
        if (existing) {
          idByText.set(text, existing);
        } else {
          const geo = res.geocoded;
          const id = await directoryApi.createAddress({
            organisationId: deps.organisationId,
            addressType: 'Location',
            name: geo.name,
            code: null,
            fullAddress: geo.fullAddress,
            placeId: geo.placeId,
            city: geo.city,
            postcode: geo.postcode,
            countryIsoCode: geo.countryIsoCode,
            latitude: geo.latitude,
            longitude: geo.longitude,
            coordinatesSource: null, // geocoded by Google with a place id - Directory infers it
            status: 'Active',
            serviceTags: null,
            partyId: null,
            siteId: null,
          });
          createdByPlaceId.set(pid, id);
          idByText.set(text, id);
        }
      } else {
        errByText.set(text, 'Address not geocoded');
      }
    } catch (err) {
      errByText.set(text, err instanceof Error ? err.message : 'Address error');
    } finally {
      created += 1;
      deps.onProgress?.(created, distinctList.length, 'Resolving addresses');
    }
  }

  // ── 2. Build trees (allocate numbers), flagging address-failed orders ─────
  const toSend: { key: string; input: ImportOrderInput }[] = [];
  for (const d of committable) {
    const colAddr = d.job.collectionAddress.trim();
    const delAddr = d.job.deliveryAddress.trim();
    const colId = idByText.get(colAddr);
    const delId = idByText.get(delAddr);
    if (!colId || !delId) {
      const which = !colId ? 'collection' : 'delivery';
      const msg = errByText.get(!colId ? colAddr : delAddr) ?? 'address unresolved';
      patch(d.key, { status_: 'failed', error: `Could not resolve ${which} address: ${msg}` });
      continue;
    }

    let orderNumber = d.orderNumber;
    if (!orderNumber) {
      try {
        orderNumber = await deps.consumeOrderNumber();
        patch(d.key, { orderNumber });
      } catch (err) {
        patch(d.key, {
          status_: 'failed',
          error: err instanceof Error ? err.message : 'Could not allocate an order number',
        });
        continue;
      }
    }

    toSend.push({ key: d.key, input: buildInput(d, orderNumber, colId, delId) });
  }

  if (toSend.length === 0) return;

  // ── 3. Bulk import + 4. write back per-order outcomes ─────────────────────
  deps.onProgress?.(0, toSend.length, 'Creating orders');
  const result = await deps.importOrders(toSend.map((t) => t.input));
  const byRef = new Map(result.results.map((r) => [r.rowRef, r]));
  for (const { key } of toSend) {
    const r = byRef.get(key);
    if (r?.isCreated) patch(key, { status_: 'created', error: undefined });
    else patch(key, { status_: 'failed', error: r?.error ?? 'Import failed' });
  }
  deps.onProgress?.(toSend.length, toSend.length, 'Creating orders');
}

/** Build the order tree: header + Collect (cargo `C1`) + Deliver (sourced from `C1`). */
function buildInput(d: OrderDraft, orderNumber: string, colId: string, delId: string): ImportOrderInput {
  const qty = d.job.quantity;
  // Loading metres for a pallet line = per-pallet metres × quantity (rounded),
  // mirroring the Order Details cargo grid; null when not a pallet variant.
  const lm =
    d.cargoLoadingMetresPerUnit != null && qty != null
      ? Number((d.cargoLoadingMetresPerUnit * qty).toFixed(2))
      : null;
  const description = d.cargoVariantName ?? null;

  return {
    rowRef: d.key,
    orderNumber,
    customerPartyId: d.customerPartyId!,
    cargoDefinitionId: d.cargoDefinitionId!,
    temperatureC: d.job.temperatureC,
    reference1: d.job.jobRef || null,
    notes: d.job.notes || null,
    tasks: [
      {
        sequence: 1,
        type: 'Collect',
        addressId: colId,
        plannedDate: dateOrNull(d.job.collectionDate),
        plannedTime: timeOrNull(d.job.collectionTime),
        cargoItems: [
          { ref: 'C1', kind: d.cargoKind, quantity: qty, description, loadingMetresM: lm },
        ],
      },
      {
        sequence: 2,
        type: 'Deliver',
        addressId: delId,
        plannedDate: dateOrNull(d.job.deliveryDate),
        plannedTime: timeOrNull(d.job.deliveryTime),
        cargoItems: [
          {
            sourceCollectionItemRef: 'C1',
            kind: d.cargoKind,
            quantity: qty,
            description,
            loadingMetresM: lm,
          },
        ],
      },
    ],
  };
}
